/** @jsx jsx */
import React, { useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { css, jsx } from '@compiled/react';
import isNil from 'lodash/isNil';
import { graphql, useFragment } from 'react-relay';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { Text, Inline, xcss, Box, media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import {
	GOALS_CF_TYPE,
	ATLAS_PROJECT_CF_TYPE,
	STATUS_TYPE,
	NUMBER_CF_TYPE,
	PRIORITY_TYPE,
	CATEGORY_TYPE,
	ASSIGNEE_TYPE,
	SPRINT_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	LABELS_TYPE,
	ISSUE_TYPE,
	GROUP_CF_TYPE,
	MULTI_GROUP_CF_TYPE,
	SELECT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	VERSION_CF_TYPE,
	MULTI_VERSION_CF_TYPE,
	AFFECTS_VERSIONS_TYPE,
	FIX_VERSIONS_TYPE,
	REPORTER_TYPE,
	USER_CF_TYPE,
	RESOLUTION_TYPE,
	LABELS_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useFlatList,
	useIsItemExpanded,
	useIsItemLoading,
	useIsSelected,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import {
	ExpandButtonWithCreateChildButton,
	ExpandButton,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/expand-button/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import CreateChildButton from '@atlassian/jira-issue-table-hierarchy/src/ui/create-child-button/index.tsx';
import { isCreateIssueSupportedForFieldType } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/index.tsx';
import { useIICConfigActions } from '@atlassian/jira-issue-table-inline-issue-create/src/controllers/config/index.tsx';
import type { TriggerPropsType } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import type { GroupHeader$key, GroupHeader$data } from './__generated__/GroupHeader.graphql';
import { CategoryGroupHeader } from './category-group-header/CategoryGroupHeader.tsx';
import messages from './messages.tsx';
import { PriorityGroupHeader } from './priority-group-header/PriorityGroupHeader.tsx';
import { SprintGroupHeader } from './sprint-group-header/SprintGroupHeader.tsx';
import { StatusGroupHeader } from './status-group-header/StatusGroupHeader.tsx';
import { StoryPointsGroupHeader } from './story-points-group-header/StoryPointsGroupHeader.tsx';
import { GoalGroupHeader } from './goal-group-header/GoalGroupHeader.tsx';
import { LabelGroupHeader } from './label-group-header/LabelGroupHeader.tsx';
import { IssueTypeGroupHeader } from './issue-type-group-header/IssueTypeGroupHeader.tsx';
import { GroupPickerGroupHeader } from './group-picker-group-header/GroupPickerGroupHeader.tsx';
import { OptionGroupHeader } from './option-group-header/OptionGroupHeader.tsx';
import { VersionGroupHeader } from './version-group-header/VersionGroupHeader.tsx';
import { UserGroupHeader } from './user-group-header/UserGroupHeader.tsx';
import { ResolutionGroupHeader } from './resolution-group-header/ResolutionGroupHeader.tsx';
import { AtlasProjectGroupHeader } from './atlas-project-group-header/AtlasProjectGroupHeader.tsx';

export type Props = {
	groupHeader: GroupHeader$key;
	groupConnectionId: string;
	isGroupAtTop?: boolean;
	columnsCount: number;
	groupId?: string;
	createChildTriggerProps?: TriggerPropsType;
	isIssueCreateEnabled?: boolean;
	isGridNavigationEnabled?: boolean;
	isStickyColumnsVisible?: boolean;
	isAutoUnstickyOnSmallScreenEnabled?: boolean;
	projectKey?: string | null;
};

const isCompletedSprintGroup = (data: GroupHeader$data) =>
	data?.fieldValue?.__typename === 'JiraJqlSprintFieldValue' &&
	data.fieldValue.sprint?.state === 'CLOSED';

const isSubtaskGroup = (data: GroupHeader$data) => {
	return (
		data.fieldValue?.__typename === 'JiraJqlIssueTypeFieldValue' &&
		data.fieldValue.issueTypes?.[0]?.hierarchy?.level === -1
	);
};

const getGroupIssueTypeId = (data: GroupHeader$data) => {
	switch (data.fieldType) {
		case ISSUE_TYPE:
			return data.fieldValue?.issueTypes?.[0]?.issueTypeId;
		default:
			return null;
	}
};

const ONBOARDING_TARGET_NAME = 'issue-navigator-create-issue-in-group';

export const GroupHeader = ({
	groupHeader,
	groupConnectionId,
	createChildTriggerProps,
	isGroupAtTop = false,
	columnsCount,
	groupId,
	isIssueCreateEnabled = true,
	isGridNavigationEnabled = false,
	isStickyColumnsVisible,
	isAutoUnstickyOnSmallScreenEnabled,
	projectKey,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tableRowRef = useRef<HTMLTableRowElement>(null);

	const data = useFragment<GroupHeader$key>(
		graphql`
			fragment GroupHeader on JiraSpreadsheetGroup
			@argumentDefinitions(
				isGroupByProjectsEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/group-by-projects.relayprovider"
				}
			) {
				id
				fieldType
				issueCount
				fieldValue {
					__typename
					...PriorityGroupHeader
					...UserGroupHeader
					...StatusGroupHeader
					...CategoryGroupHeader
					...OptionGroupHeader
					...VersionGroupHeader
					...SprintGroupHeader
					...StoryPointsGroupHeader
					...GoalGroupHeader
					...AtlasProjectGroupHeader @include(if: $isGroupByProjectsEnabled)
					...LabelGroupHeader
					...IssueTypeGroupHeader
					...GroupPickerGroupHeader
					...ResolutionGroupHeader
					... on JiraJqlIssueTypeFieldValue {
						issueTypes {
							issueTypeId
							hierarchy {
								level
							}
						}
					}
					... on JiraJqlSprintFieldValue {
						sprint {
							state
						}
					}
				}
			}
		`,
		groupHeader,
	);

	const itemDetails = useMemo(
		() => (data ? { itemsConnectionId: groupConnectionId, itemId: data.id, groupId } : null),
		[data, groupConnectionId, groupId],
	);

	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const list = fg('nin-grid-keyboard-navigation') ? useFlatList() : [];

	const [isSelected, { setSelectedRow }] = useIsSelected(itemDetails);

	useLayoutEffect(() => {
		// Scroll the table row into view (if needed - block: nearest) when it becomes selected
		if (isSelected) {
			tableRowRef.current?.scrollIntoView({ block: 'nearest' });
		}
	}, [isSelected]);

	const { setDefaultIssueType } = useIICConfigActions();
	const groupIssueTypeId = getGroupIssueTypeId(data);

	const focusCurrentRow = useCallback(() => {
		if (groupIssueTypeId && projectKey && fg('group_by_any_field_issue_type_iic_fix')) {
			setDefaultIssueType(projectKey, groupIssueTypeId);
		}

		return setSelectedRow(itemDetails);
	}, [setSelectedRow, itemDetails, groupIssueTypeId, projectKey, setDefaultIssueType]);

	const groupHeaderView = useMemo(() => {
		switch (data.fieldType) {
			case PRIORITY_TYPE:
				return <PriorityGroupHeader priorityGroupHeader={data.fieldValue ?? null} />;
			case REPORTER_TYPE:
			case ASSIGNEE_TYPE:
			case USER_CF_TYPE:
				return (
					<UserGroupHeader userGroupHeader={data.fieldValue ?? null} fieldType={data.fieldType} />
				);
			case STATUS_TYPE:
				return <StatusGroupHeader statusGroupHeader={data.fieldValue ?? null} />;
			case NUMBER_CF_TYPE:
			case STORY_POINT_ESTIMATE_CF_TYPE:
				return <StoryPointsGroupHeader storyPointsGroupHeader={data.fieldValue ?? null} />;
			case GOALS_CF_TYPE:
				return <GoalGroupHeader goalGroupHeader={data.fieldValue ?? null} />;
			case ATLAS_PROJECT_CF_TYPE:
				return <AtlasProjectGroupHeader atlasProjectGroupHeader={data.fieldValue ?? null} />;
			case CATEGORY_TYPE:
				return <CategoryGroupHeader categoryGroupHeader={data.fieldValue ?? null} />;
			case SELECT_CF_TYPE:
			case MULTI_SELECT_CF_TYPE:
			case MULTI_CHECKBOXES_CF_TYPE:
			case RADIO_BUTTONS_CF_TYPE:
				return (
					<OptionGroupHeader
						optionGroupHeader={data.fieldValue ?? null}
						fieldType={data.fieldType}
					/>
				);
			case SPRINT_TYPE:
				return <SprintGroupHeader sprintGroupHeader={data.fieldValue ?? null} />;
			case LABELS_TYPE:
				return <LabelGroupHeader labelGroupHeader={data.fieldValue ?? null} />;
			// TODO: combine with LABELS_TYPE once we've cleaned up the group_by_any_field flags
			case LABELS_CF_TYPE:
				return fg('group_by_any_field') ? (
					<LabelGroupHeader labelGroupHeader={data.fieldValue ?? null} />
				) : null;
			case ISSUE_TYPE:
				return <IssueTypeGroupHeader issueTypeGroupHeader={data.fieldValue ?? null} />;
			case GROUP_CF_TYPE:
			case MULTI_GROUP_CF_TYPE:
				return <GroupPickerGroupHeader groupPickerGroupHeader={data.fieldValue ?? null} />;
			case VERSION_CF_TYPE:
			case MULTI_VERSION_CF_TYPE:
			case AFFECTS_VERSIONS_TYPE:
			case FIX_VERSIONS_TYPE:
				return (
					<VersionGroupHeader
						versionGroupHeader={data.fieldValue ?? null}
						fieldType={data.fieldType}
					/>
				);
			case RESOLUTION_TYPE:
				return <ResolutionGroupHeader resolutionGroupHeader={data.fieldValue ?? null} />;
			default:
				// should never happen
				return null;
		}
	}, [data]);

	const groupMetadata = useMemo(() => {
		return (
			<>
				{groupHeaderView}
				{!isNil(data?.issueCount) && (
					<Text color="color.text.subtlest">
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.issuesIssueTermRefresh
								: messages.issues,
							{ count: data.issueCount },
						)}
					</Text>
				)}
			</>
		);
	}, [data.issueCount, formatMessage, groupHeaderView]);

	const [isExpanded, { toggleIsItemExpanded }] = useIsItemExpanded(itemDetails);
	const [isLoading] = useIsItemLoading(data?.id ?? null);

	const onCreateChildClick = useCallback(() => {
		if (groupIssueTypeId && projectKey && !fg('group_by_any_field_issue_type_iic_fix')) {
			setDefaultIssueType(projectKey, groupIssueTypeId);
		}
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'groupCreateChildButton');
		if (!isExpanded) {
			toggleIsItemExpanded(itemDetails, 0);
		}
		createChildTriggerProps?.onClick();
	}, [
		createAnalyticsEvent,
		isExpanded,
		toggleIsItemExpanded,
		itemDetails,
		createChildTriggerProps,
		setDefaultIssueType,
		groupIssueTypeId,
		projectKey,
	]);

	const headerContent = useMemo(() => {
		if (expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) {
			return (
				<>
					{data.issueCount ? (
						<ExpandButton
							itemId={data.id}
							itemsConnectionId={groupConnectionId}
							depth={0}
							isGroupButton
							groupId={groupId}
						/>
					) : null}
					{groupMetadata}
					{isIssueCreateEnabled &&
						isCreateIssueSupportedForFieldType(data.fieldType) &&
						!isCompletedSprintGroup(data) &&
						!isSubtaskGroup(data) &&
						(isGroupAtTop ? (
							<SpotlightManager>
								<SpotlightTarget name={ONBOARDING_TARGET_NAME}>
									<div css={raiseOnHoverStyles}>
										<CreateChildButton
											onClick={onCreateChildClick}
											ref={createChildTriggerProps?.ref}
											isCreateWorkItem
										/>
									</div>
								</SpotlightTarget>
								<EngagementSpotlight engagementId={ONBOARDING_TARGET_NAME} />
							</SpotlightManager>
						) : (
							<div css={raiseOnHoverStyles}>
								<CreateChildButton
									onClick={onCreateChildClick}
									ref={createChildTriggerProps?.ref}
									isCreateWorkItem
								/>
							</div>
						))}
				</>
			);
		}

		return (
			<>
				{data.issueCount ? (
					<ExpandButtonWithCreateChildButton
						isGroupButton
						itemId={data.id}
						itemsConnectionId={groupConnectionId}
						depth={0}
						groupId={groupId}
					/>
				) : null}
				{groupMetadata}
			</>
		);
	}, [
		data,
		groupConnectionId,
		groupId,
		groupMetadata,
		isIssueCreateEnabled,
		isGroupAtTop,
		onCreateChildClick,
		createChildTriggerProps?.ref,
	]);

	const rowIndex =
		itemDetails && fg('nin-grid-keyboard-navigation')
			? list.indexOf(`${itemDetails.itemId}::${itemDetails.itemId}`) + 1
			: -1;
	return (
		<tr
			role="row"
			tabIndex={-1}
			{...(rowIndex > 0 &&
				isGridNavigationEnabled && {
					'aria-rowindex': rowIndex,
				})}
			css={[
				headerRowStyles,
				isSelected && !isGridNavigationEnabled && activeRowStyles,
				isGridNavigationEnabled && headerRowKeyboardNavigationStyles,
			]}
			onFocus={focusCurrentRow}
			data-testid="issue-table-grouping.ui.group-row.group-header.tr"
			aria-selected={isSelected}
			ref={tableRowRef}
		>
			<Box
				as="td"
				// Additional column for configure columns menu & issue actions
				colSpan={columnsCount + 1}
				xcss={[
					borderStyles,
					isGroupAtTop && removeTopBorder,
					rowHeight,
					(!isExpanded ||
						isLoading ||
						!data.issueCount ||
						expVal('jira_spreadsheet_issue_create_in_group', 'isEnabled', false)) &&
						removeBottomBorder,
				]}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...(isGridNavigationEnabled && {
					tabIndex: -1,
				})}
			>
				{isStickyColumnsVisible && fg('issue_table_frozen_columns') ? (
					<Box
						testId="issue-table-grouping.ui.group-row.group-header.sticky-header"
						xcss={
							// eslint-disable-next-line no-nested-ternary
							fg('jira_list_frozen_column_bugfix')
								? isAutoUnstickyOnSmallScreenEnabled
									? stickyContainerResponsiveStyles
									: stickyContainerStyles
								: stickyContainerStylesOld
						}
					>
						<Inline xcss={[inlineStyles, !data.issueCount && placeholderStyles]} space="space.100">
							{headerContent}
						</Inline>
					</Box>
				) : (
					<Inline xcss={[inlineStyles, !data.issueCount && placeholderStyles]} space="space.100">
						{headerContent}
					</Inline>
				)}
			</Box>
		</tr>
	);
};

const inlineStyles = xcss({
	paddingInline: 'space.150',
	alignItems: 'center',
	gap: 'space.100',
});

const placeholderStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	marginLeft: 'space.400',
});

const stickyContainerStylesOld = xcss({
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: 'space.0',
	width: 'fit-content',
});

const stickyContainerStyles = xcss({
	position: 'sticky',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: 'space.0',
	width: 'fit-content',
});

const stickyContainerResponsiveStyles = xcss({
	[media.above.md]: {
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
		left: 'space.0',
		width: 'fit-content',
	},
});

const borderStyles = xcss({
	border: 0,
	borderColor: 'color.border',
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: 'border.width',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderBottomWidth: 'border.width',
});

const removeTopBorder = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderTopWidth: '0',
});

const removeBottomBorder = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderBottomWidth: '0',
});

const headerRowStyles = css({
	'&:hover': {
		backgroundColor: token('elevation.surface.hovered'),
	},
	'&:focus': {
		outline: 'none',
	},
});

const headerRowKeyboardNavigationStyles = css({
	'&:focus-within': {
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
});

const activeRowStyles = css({
	backgroundColor: token('color.background.selected'),
	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
	},
});

const raiseOnHoverStyles = css({
	position: 'relative',
	'&:hover': {
		zIndex: '2',
	},
});

const rowHeight = xcss({
	height: token('space.500'),
});
